import type { IconName } from "@repo/fika-design-system/icon"
import type { Accessor } from "solid-js"
import { Show } from "solid-js"
import { Form } from "@/designSystem/components/form"
import { Text } from "@/designSystem/components/text"
import { NameInputField } from "@/designSystem/patterns/name-input-field"
import { graphql } from "@/graphql"
import { useI18nContext } from "@/i18n/i18n-solid"
import { createName } from "@/lib/create-name"
import {
  createMutationWithErrorHandling,
  ErrorFragment,
} from "@/lib/graphql-mutation-helpers"
import { toast } from "@/lib/toast"

interface PublicationNameFormProps {
  onAfterSave?: () => void | Promise<void>
  title: string
  id?: string
  iconName: IconName
  nameChangeCooldownDays?: Accessor<number | null | undefined>
}

export function PublicationNameForm(props: PublicationNameFormProps) {
  const { LL } = useI18nContext()

  const UpsertPublicationMutation = graphql(
    `mutation UpsertPublicationSettings($input: UpsertPublicationInput!) {
      upsertPublication(input: $input) {
        ... on MutationUpsertPublicationSuccess {
          data {
            id
            publication {
              id
              name
              nameChangeCooldownDays
            }
          }
        }
        ...ErrorFragment
      }
    }`,
    [ErrorFragment],
  )
  const [upsertPublicationState, upsertPublication] =
    createMutationWithErrorHandling(
      UpsertPublicationMutation,
      "upsertPublication",
    )

  const {
    userName,
    setUserName,
    nameDirty,
    validName,
    isFormValid,
    isButtonDisabled,
    nameStatus,
  } = createName()

  const cooldownDays = () => props.nameChangeCooldownDays?.() ?? null
  const isCooldownActive = () => {
    const days = cooldownDays()
    return days != null && days > 0
  }

  async function onSubmit() {
    const state = await upsertPublication({ input: { name: userName() } })
    if (state.success) {
      toast.success(LL().settings.informationSaved())
      await props?.onAfterSave?.()
    }
  }

  return (
    <Form
      title={props.title}
      id={props.id}
      description={LL().blog.blogNameDescription()}
      hue="amber"
      iconName={props.iconName}
      onSubmit={onSubmit}
    >
      <Show when={isCooldownActive()}>
        <Text fontSize="s" color="sand600">
          {LL().blog.nameChangeCooldown({ days: cooldownDays()! })}
        </Text>
      </Show>
      <NameInputField
        userName={userName}
        setUserName={setUserName}
        nameStatus={nameStatus}
        nameDirty={nameDirty}
        validName={validName}
        isFormValid={isFormValid}
        disabled={isCooldownActive()}
      />
      <Show when={nameDirty()}>
        <Text fontSize="s" color="amber700">
          {LL().blog.nameChangeWarning()}
        </Text>
      </Show>
      <Form.Save
        hue="amber"
        disabled={isButtonDisabled() || isCooldownActive()}
        active={upsertPublicationState.fetching}
      />
    </Form>
  )
}
